import * as chrono from 'chrono-node';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface IRegion {
    startIndex: number;
    length: number;
    parsedDate: Date | null;
    key: string;
    text: string;
    type?: 'phone' | 'email' | 'date';
}

export const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
export const PHONE_REGEX = /((\+?\d{1,3}|\d{1,3})[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

export function parseMessageLineIntoRegions(line: string, lineIndex: number): IRegion[] {
    const regions: IRegion[] = [];

    // Create a map of all matches with their positions
    const matches: Array<{ type: 'email' | 'phone' | 'date', text: string, index: number, parsedDate?: Date }> = [];

    // First parse dates
    const dateMatches = chrono.parse(line);
    if (dateMatches.length > 0) {
        for (let dateI = 0; dateI < dateMatches.length; dateI++) {
            const dateRange = dateMatches[dateI];
            const parsedDate = dateRange.date();

            // add a little bit of intelligence to the date parsing
            // specifically we want the times to default to likely user intention
            // so if the user types "tomorrow at 2" we want to default to 2pm
            if (!dateRange.text.toLowerCase().includes('am') && !dateRange.text.toLowerCase().includes('pm')) {
                // am/pm not explicitly specified
                // so we need to guess
                const hour = parsedDate.getHours();
                if (hour <= 6) {
                    // assume anything before 6am is pm
                    parsedDate.setHours(hour + 12);
                }
            }

            matches.push({
                type: 'date',
                text: dateRange.text,
                index: dateRange.index,
                parsedDate: parsedDate,
            });
        }
    }

    // Add email matches
    const emailMatches = Array.from(line.matchAll(EMAIL_REGEX));
    for (const match of emailMatches) {
        matches.push({
            type: 'email',
            text: match[0],
            index: match.index!
        });
    }

    // Add phone matches
    const phoneMatches = Array.from(line.matchAll(PHONE_REGEX));
    for (const match of phoneMatches) {
        const parsedPhone = parsePhoneNumberFromString(match[0], 'US');
        if (parsedPhone?.isValid()) {
            matches.push({
                type: 'phone',
                text: match[0],
                index: match.index!
            });
        }
    }

    // Sort matches by their position in the text
    matches.sort((a, b) => a.index - b.index);

    const firstMatch = matches[0];
    // Add text before the match
    if (firstMatch) {
        const beforeText = line.substring(0, firstMatch.index);
        regions.push({
            key: `before-${firstMatch.type}-${firstMatch.index}`,
            text: beforeText,
            startIndex: 0,
            length: beforeText.length,
            parsedDate: null,
        });
    }
    // Process matches in order
    for (let idx = 0; idx < matches.length; idx++) {
        const match = matches[idx];
        // Add the match
        regions.push({
            key: `${match.type}-${match.index}`,
            text: match.text,
            startIndex: match.index,
            length: match.text.length,
            parsedDate: match.parsedDate || null,
            type: match.type,
        });

        const nextMatch = matches[idx + 1];

        if (nextMatch) {
            regions.push({
                key: `remaining-text-${lineIndex}`,
                text: line.substring(match.index + match.text.length, nextMatch.index),
                startIndex: match.index + match.text.length,
                length: nextMatch.index - (match.index + match.text.length),
                parsedDate: null,
            });
        } else {
            regions.push({
                key: `remaining-text-${lineIndex}`,
                text: line.substring(match.index + match.text.length, line.length),
                startIndex: match.index + match.text.length,
                length: line.length - (match.index + match.text.length),
                parsedDate: null,
            });
        }
    }

    // If no regions were found, return the whole line as one region
    if (regions.length === 0) {
        return [{
            key: `line-section-${lineIndex}`,
            text: line,
            startIndex: 0,
            length: line.length,
            parsedDate: null,
        }];
    }

    return regions;
}